// React
import { useEffect, useCallback } from 'react'

// React Router Dom
import { useHistory } from 'react-router-dom'

// MRL
import { observer } from 'mobx-react-lite'

// i18n
import { useTranslation } from 'react-i18next'

// Constant
import { AUTH_URL, PERSONAL_DATA_URL, HOME_URL } from 'constant/url.constant'
import { AUTHORIZATION } from 'constant/request-header.constant'
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_AUTHENTICATED_USER,
  LOCAL_STORAGE_IS_AUTHENTICATED,
  LOCAL_STORAGE_REFRESH_TOKEN,
} from 'constant/local-storage.constant'

// Utils
import { oidc_userManager } from 'utils'
import { messageUtils_success, messageUtils_error } from 'utils/common/message.utils'
import { axios } from 'utils'

// Custom Hook
import { useAuth } from 'hooks'

// OIDC Client
import { UserManager } from 'oidc-client'

const Authorize = observer(() => {
  // Hook
  const { t } = useTranslation()
  const {
    auth_persisted,
    auth_INITIALIZE,
    auth_SET_AUTHENTICATED,
    auth_getAuthenticatedUser,
    auth_changeRole,
    auth_setToken,
    auth_logout,
  } = useAuth()
  const history = useHistory()

  // Do anything with authentication
  useEffect(() => {
    initAuthentication()

    // eslint-disable-next-line
  }, [])

  // Do anything with OIDC
  useEffect(() => {
    ;(async () => {
      const silentUserManager = oidc_userManager(true)

      // When token will be expired
      silentUserManager.events.addAccessTokenExpiring(async () => handleRefreshToken(silentUserManager))

      // When token expired
      silentUserManager.events.addAccessTokenExpired(async () => handleRefreshToken(silentUserManager))
    })()

    // eslint-disable-next-line
  }, [])

  /**
   * @description Handle role
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleRole = async (): Promise<void> => {
    const isAllInactive = auth_persisted?.auth_authenticatedUser?.roles?.every(role => !role.status)
    const isSomeActive = auth_persisted?.auth_authenticatedUser?.roles?.some(role => role.status)

    if (isSomeActive) {
      // If user previously activate some role, make true
      auth_SET_AUTHENTICATED(true)
      localStorage.setItem(LOCAL_STORAGE_IS_AUTHENTICATED, 'true')

      if (!auth_persisted?.auth_isAuthenticated) history.replace(PERSONAL_DATA_URL.PROFILE)

      return
    }

    // Check for multiple roles, then redirect to change role page
    if (auth_persisted?.auth_authenticatedUser?.roles?.length! > 1 && isAllInactive) {
      history.replace(AUTH_URL.CHANGE_ROLE)

      return
    }

    // Otherwise, just pass the page, but hit the change role request
    if (auth_persisted?.auth_authenticatedUser?.roles?.length === 1) {
      await auth_changeRole(auth_persisted?.auth_authenticatedUser?.roles?.[0].id!)

      // redirect to profile page
      history.replace(PERSONAL_DATA_URL.PROFILE)

      return
    }

    // If, user didn't have role throw error message
    if (auth_persisted?.auth_authenticatedUser?.roles?.length === 0) {
      auth_SET_AUTHENTICATED(true)
      localStorage.setItem(LOCAL_STORAGE_IS_AUTHENTICATED, 'true')

      // redirect to profile page
      history.replace(PERSONAL_DATA_URL.PROFILE)

      return
    }
  }

  /**
   * @description Get authenticated user
   *
   * @return {Promise<void>} Promise<void>
   */
  const getAuthenticatedUser = useCallback(async (): Promise<void> => {
    try {
      // Load authenticated user from server
      await auth_getAuthenticatedUser()
      await handleRole()

      messageUtils_success(t('app.alert.userVerified'))
    } catch (err) {
      const error = err as any
      if (error?.status !== 'ECONNABORTED') {
        history.replace(HOME_URL.INDEX)

        auth_logout()
      }
    }

    // eslint-disable-next-line
  }, [history, auth_persisted])

  /**
   * @description Init authentication
   *
   * @return {Promise<void>} Promise<void>
   */
  const initAuthentication = useCallback(async (): Promise<void> => {
    const auth_accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
    const auth_refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN) || ''
    const auth_authenticatedUser = localStorage.getItem(LOCAL_STORAGE_AUTHENTICATED_USER) || '{}'
    const auth_isAuthenticated = Boolean(localStorage.getItem(LOCAL_STORAGE_IS_AUTHENTICATED)) || false

    if (auth_accessToken) {
      auth_INITIALIZE({
        auth_isAuthenticated,
        auth_accessToken,
        auth_refreshToken,
        auth_authenticatedUser: JSON.parse(auth_authenticatedUser),
      })

      getAuthenticatedUser()
    }
  }, [auth_INITIALIZE, getAuthenticatedUser])

  /**
   * @description Handle refresh token
   *
   * @param {UserManager} silentUserManager
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleRefreshToken = useCallback(
    async (silentUserManager: UserManager): Promise<void> => {
      console.log('Token Refreshing...')

      try {
        // Get User From Silent Sign In
        const { access_token: accessToken, refresh_token: refreshToken } = await silentUserManager.signinSilent()

        // Set The Token
        auth_setToken({ accessToken, refreshToken: refreshToken || '' })

        // Set The Access Token To Axios Headers
        axios.defaults.headers.common[AUTHORIZATION] = `Bearer ${accessToken}`

        console.log('Token Refreshed')
      } catch (err) {
        auth_logout()

        history.replace(HOME_URL.INDEX)

        console.error(t('auth.duende.tokenExpired'))

        messageUtils_error(t('auth.duende.tokenExpired'))
      }
    },
    [auth_setToken, auth_logout, history, t],
  )

  return (
    <></>
  )
})

export { Authorize }
